import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import {
  setTransactionEndDate,
  setTransactionPage,
  setTransactionResults,
  setTransactionStartDate,
  setTransactionValidateTransaction,
} from 'src/app/action/transaction.actions';
import { PageSize } from 'src/app/enumeration/pageSize';
import {
  selectEndDate,
  selectPage,
  selectResults,
  selectStartDate,
  selectValidateTransaction,
} from 'src/app/selector/transaction.selectors';
import { TransactionApiService } from 'src/app/service/transaction/transaction-api.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';
import { DownloadService } from 'src/app/utils/download/download.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

// Interface mapping to strictly enforce KBZPay transaction schemas
export interface KbzpayPaymentTransactionResponse {
  kbzpayPaymentTransactionRespLst: KbzpayPaymentTransactionRespLst[]
  nextContinuation: number
  pageTotal: number
  allTotal: number
}

export interface KbzpayPaymentTransactionRespLst {
  srNo: number
  paymentPhone: string
  paymentName: string
  errorResponse?: string
  errorStatusCode?: string
  result?: string
  code?: string
  msg?: string
  totalAmount?: string
  transCurrency?: string
  merchOrderId?: string
  paymentOrderId?: string
  mmOrderId?: string
  tradeStatus?: string
  paySuccessTime: string
}


@Component({
  selector: 'app-transaction',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, FormsModule, DateFormatPipe],
  templateUrl: './transaction.component.html',
  styleUrl: './transaction.component.css',
})
export class TransactionComponent implements OnInit {
  // Functional constructor-less injection tokens engine
  private readonly apiService = inject(TransactionApiService);
  private readonly datePipe = inject(DatePipe);
  private readonly downloadService = inject(DownloadService);
  private readonly store = inject(Store);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link global layout theme visibility pointer cleanly
  readonly isDarkMode = this.themeService.isDarkMode;

  // Fully refactored component state variables using reactive signals
  readonly kbzpayPaymentTransactionRespLst = signal<KbzpayPaymentTransactionRespLst[]>([]);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // 1-based index optimization to support seamless ngx-pagination UI matching
  readonly pageSize = signal<number>(PageSize.TRANSACTION_LISTS);
  
  readonly loading = signal<boolean>(false);
  readonly apiDownloadLoading = signal<boolean>(false);

  // Filter conditions models
  results = 'ALL';
  validateTransaction = 'ALL';
  startDate = '';
  endDate = '';
  startDateString: string | null = null;
  endDateString: string | null = null;
  
  downloadFileName = '';
  readonly limit = 10000;
  
  ngOnInit(): void {
    this.restorePreviousDataReloadWithAppState();
    this.fetchTransactionRecords();
  }

  /**
   * Subscribes safely to NgRx state selectors to reconstruct state contexts upon view reload
   */
  private restorePreviousDataReloadWithAppState(): void {
    // If your NgRx version fully supports Signals, replace these lines with: this.store.selectSignal(selectResults)
    this.store.select(selectResults).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => this.results = res || 'ALL');
    this.store.select(selectValidateTransaction).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => this.validateTransaction = val || 'ALL');
    this.store.select(selectStartDate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(start => this.startDate = start || '');
    this.store.select(selectEndDate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(end => this.endDate = end || '');
    
    this.store.select(selectPage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(savedPage => {
      // Syncs 0-based store values back seamlessly to our 1-based visual layout index components
      this.page.set((savedPage ?? 0) + 1);
    });
  }

  /**
   * Streams page-indexed ledger transaction lists down from your production cluster endpoints
   */
  fetchTransactionRecords(): void {
    this.loading.set(true);
    
    // Server adjustment conversion parameter matching
    const zeroBasedPage = this.page() - 1;

    this.apiService.getTransactionLists(
      this.results,
      this.validateTransaction,
      this.startDateString,
      this.endDateString,
      zeroBasedPage
    )
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false)) // Guarantees loading state suppression on both win and failure
    )
    .subscribe({
      next: (data: KbzpayPaymentTransactionResponse) => {
        this.kbzpayPaymentTransactionRespLst.set(data?.kbzpayPaymentTransactionRespLst || []);
        this.allTotal.set(data?.allTotal || 0);
      },
      error: (err: any) => {
        console.error('KBZPay Transaction Retrieval Pipeline Failure Exception:', err);
        alert(err?.message || err || 'Network failure encountered while fetching transaction profiles.');
      },
      complete: () => {
        this.saveCurrentStateToNgrxStore();
      }
    });
  }

  /**
   * Audits chronological filter boundaries before processing network operations queries
   */
  saveFilter(): boolean {
    if (!this.validateDateRangeBoundaries()) return false;
    
    this.page.set(1); // Reset dashboard viewing indexing parameters back to step 1 upon parameter revision
    this.fetchTransactionRecords();
    return true;
  }

  /**
   * Extracts historical transaction statements directly into a local reporting spreadsheet binary document (.csv)
   */
  downloadFile(): void {
    if (!this.validateDateRangeBoundaries()) return;

    if (!this.downloadFileName) {
      alert('Please enter a valid export target filename setup configuration.');
      return;
    }

    if (!this.downloadFileName.toLowerCase().endsWith('.csv')) {
      this.downloadFileName += '.csv';
    }

    this.apiDownloadLoading.set(true);

    this.apiService.getDownloadCSV(
      this.downloadFileName,
      this.results,
      this.validateTransaction,
      this.startDateString,
      this.endDateString,
      this.limit
    )
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.apiDownloadLoading.set(false))
    )
    .subscribe({
      next: (blobPayload: any) => {
        this.downloadService.downloadCsvWithModifiedTime(blobPayload, this.downloadFileName);
        this.downloadFileName = ''; // Clean input fields only upon successful download
        this.closeModalViaNativeElement('downloadCSVModal');
      },
      error: (err: any) => {
        console.error('Transaction reporting matrix streaming execution failure:', err);
        alert(err?.message || err || 'Critical fault intercept while downloading ledger reports.');
      }
    });
  }

  /**
   * Captures user-triggered layout click navigation inputs to cycle data indices safely
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.fetchTransactionRecords();
    }
  }

  /**
   * Validates date selections and applies uniform string transformation rules safely
   */
  private validateDateRangeBoundaries(): boolean {
    if (!this.startDate && this.endDate) {
      alert('Please select start date');
      return false;
    }
    if (this.startDate && !this.endDate) {
      alert('Please select end date');
      return false;
    }

    if (this.startDate && this.endDate) {
      this.startDateString = this.datePipe.transform(this.startDate, 'dd/MM/yyyy');
      this.endDateString = this.datePipe.transform(this.endDate, 'dd/MM/yyyy');
    } else {
      this.startDateString = null;
      this.endDateString = null;
    }
    
    return true;
  }

  /**
   * Keeps state histories updated centrally inside your state container layer
   */
  private saveCurrentStateToNgrxStore(): void {
    const zeroBasedPage = this.page() - 1;

    this.store.dispatch(setTransactionResults({ transactionResults: this.results }));
    this.store.dispatch(setTransactionValidateTransaction({ transactionValidateTransaction: this.validateTransaction }));
    this.store.dispatch(setTransactionStartDate({ transactionStartDate: this.startDate }));
    this.store.dispatch(setTransactionEndDate({ transactionEndDate: this.endDate }));
    this.store.dispatch(setTransactionPage({ transactionPage: zeroBasedPage }));
  }

  /**
   * Vanilla DOM helper abstract to dismiss operational bootstrap components cleanly without legacy jQuery dependencies
   */
  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }
}
